<template>
  <div class="my-contract-detail" v-loading="loading">
    <div class="page-header">
      <h2>계약서 확인</h2>
      <div>
        <el-button @click="$router.push('/my-contracts')">목록</el-button>
        <el-button @click="helpVisible = true">
          <el-icon><QuestionFilled /></el-icon>
          도움말
        </el-button>
      </div>
    </div>

    <template v-if="detail">
      <!-- 계약서 정보 -->
      <el-card class="info-card">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="계약번호">{{ detail.contract_number }}</el-descriptions-item>
          <el-descriptions-item label="상태">
            <el-tag :type="statusTag(detail.status)">{{ detail.status_display }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="계약서명">{{ detail.contract_title }}</el-descriptions-item>
          <el-descriptions-item label="확인기한">{{ detail.deadline?.substring(0, 16) }}</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 진행 흐름: 지금 단계 + 다음 할 일 -->
      <el-card class="info-card">
        <ContractFlowSteps mode="employee" :detail="detail" :read-seconds="totalReadTime" />
      </el-card>

      <!-- 계약 조건 요약 (양식에 항목이 있을 때) -->
      <el-card v-if="detail.terms_rows?.length" class="info-card">
        <template #header><h3>계약 조건</h3></template>
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item v-for="r in detail.terms_rows" :key="r.key" :label="r.label">{{ r.value }}</el-descriptions-item>
        </el-descriptions>
        <div class="terms-note">아래 문서 본문과 함께 확인하세요. 동의하면 이 조건이 그대로 기록됩니다.</div>
      </el-card>

      <!-- PDF 뷰어 영역 -->
      <el-card class="info-card viewer-card">
        <template #header><h3>계약서 내용</h3></template>
        <div class="pdf-viewer" ref="pdfViewerRef" @scroll="handleScroll">
          <iframe
            v-if="detail.document_file_url"
            :src="detail.document_file_url"
            style="width:100%; height:600px; border:none;"
          />
          <div v-else class="no-file">계약서 파일이 아직 준비되지 않았습니다.</div>
        </div>

        <!-- 열람 상태 표시 -->
        <div class="read-status">
          <el-tag :type="detail.scroll_completed ? 'success' : 'info'" size="small">
            {{ detail.scroll_completed ? '전체 열람 완료' : '열람 중...' }}
          </el-tag>
          <span class="read-time">
            열람 시간: {{ totalReadTime }}초 / {{ detail.minimum_read_seconds }}초
            <template v-if="remainingSeconds > 0">(<strong>{{ remainingSeconds }}초</strong> 남음)</template>
          </span>
          <el-button
            v-if="detail.require_full_read && !detail.scroll_completed"
            type="primary"
            plain
            size="small"
            :icon="Check"
            @click="markScrollComplete"
          >
            전체 확인 완료
          </el-button>
        </div>
      </el-card>

      <!-- 동의 영역 (미동의 상태에서만) -->
      <el-card v-if="canShowConsent" class="info-card consent-card">
        <template #header><h3>동의</h3></template>

        <!-- 동의 항목 체크박스 -->
        <div class="consent-items">
          <div
            v-for="(item, idx) in detail.consent_items"
            :key="idx"
            class="consent-item"
          >
            <el-checkbox
              v-model="consentChecked[`item_${idx}`]"
              :disabled="detail.status === 'CONSENTED'"
            >
              {{ item.label }}
              <el-tag v-if="item.required" type="danger" size="small" style="margin-left:4px">필수</el-tag>
            </el-checkbox>
          </div>
        </div>

        <!-- 서명 (양식이 요구할 때) -->
        <div v-if="detail.require_signature" class="signature-block">
          <div class="signature-title">서명 <el-tag type="danger" size="small">필수</el-tag></div>
          <div class="signature-desc">아래 칸에 자필 서명해 주세요. 서명은 회사 직인과 함께 계약서 완성본에 들어갑니다.</div>
          <SignaturePad v-model="signatureData" :signer-name="myName" />
        </div>

        <!-- 동의 불가 사유 표시 -->
        <el-alert
          v-if="liveConsentErrors.length > 0"
          type="warning"
          :closable="false"
          style="margin:12px 0"
        >
          <ul style="margin:0; padding-left:16px;">
            <li v-for="err in liveConsentErrors" :key="err">{{ err }}</li>
          </ul>
        </el-alert>

        <!-- 동의/거부 버튼 -->
        <div class="consent-actions">
          <el-button
            type="primary"
            size="large"
            :disabled="!liveCanConsent"
            @click="handleConsent"
            :loading="consenting"
          >
            동의합니다
          </el-button>
          <el-button
            type="danger"
            size="large"
            plain
            @click="handleReject"
          >
            거부
          </el-button>
        </div>
      </el-card>

      <!-- 동의 완료 표시 -->
      <el-card v-if="detail.status === 'CONSENTED'" class="info-card">
        <el-result icon="success" title="동의 완료" :sub-title="`${detail.consented_at?.substring(0, 16)}에 동의하셨습니다.`">
          <template #extra>
            <el-button type="primary" :icon="Download" :loading="downloading" @click="downloadSigned">
              서명 완성본 내려받기 (PDF)
            </el-button>
            <div class="signed-hint">회사 직인과 본인 서명, 동의 기록이 포함된 계약서 사본입니다. 보관해 두세요.</div>
          </template>
        </el-result>
      </el-card>
    </template>

    <!-- 도움말 패널 -->
    <HelpPanel v-model="helpVisible" :content-path="'screens/my-contract'" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, onDeactivated, onActivated } from "vue";
import { useRoute } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import { useContractStore } from "@/stores/contractStore";
import { QuestionFilled, Check, Download } from "@element-plus/icons-vue";
import SignaturePad from "@/components/contract/SignaturePad.vue";
import { useUserStore } from "@/stores/user";
import HelpPanel from "@/components/help/HelpPanel.vue";
import ContractFlowSteps from "@/components/contract/ContractFlowSteps.vue";

const helpVisible = ref(false);

const route = useRoute();
const contractStore = useContractStore();
const partyId = route.params.partyId;

const loading = ref(false);
const consenting = ref(false);
const detail = ref(null);
const consentChecked = ref({});
const sessionStart = ref(null);
const pdfViewerRef = ref(null);

// 1초 틱 — 열람 시간·남은 시간·동의 버튼 활성이 실시간으로 바뀌도록
const now = ref(Date.now());
let ticker = null;

const totalReadTime = computed(() => {
  const elapsed = sessionStart.value ? Math.floor((now.value - sessionStart.value) / 1000) : 0;
  return (detail.value?.total_read_seconds || 0) + elapsed;
});
const remainingSeconds = computed(() =>
  Math.max(0, (detail.value?.minimum_read_seconds || 0) - totalReadTime.value)
);

// 동의 가능 여부는 서버가 준 값(열람종료 전엔 시간이 0)이 아니라 지금 화면 기준으로 판단한다.
// 서버는 동의 요청에 실린 session_seconds를 합산한 뒤 같은 규칙으로 다시 검증하므로 결과는 일치한다.
const liveConsentErrors = computed(() => {
  const d = detail.value;
  if (!d) return [];
  const errs = (d.consent_errors || []).filter(
    (e) => !e.includes("끝까지 열람") && !e.includes("더 열람")
  ); // 기한 만료 등 서버만 아는 사유는 유지
  if (d.require_full_read && !d.scroll_completed) errs.push("문서를 끝까지 읽은 뒤 [전체 확인 완료]를 눌러 주세요.");
  if (remainingSeconds.value > 0) errs.push(`계약서를 ${remainingSeconds.value}초 이상 더 열람해 주세요.`);
  return errs;
});
const signatureData = ref("");
const userStore = useUserStore();
const myName = computed(() => {
  const p = userStore.profile || {};
  return `${p.last_name || ""}${p.first_name || ""}` || p.username || "";
});

const liveCanConsent = computed(() => liveConsentErrors.value.length === 0);

const downloading = ref(false);
async function downloadSigned() {
  downloading.value = true;
  try {
    await contractStore.downloadSignedDocument(
      `/my-contracts/${partyId}/signed-document/`,
      `${detail.value.contract_number}_서명완성본.pdf`
    );
  } catch (e) {
    // blob 응답이라 에러 본문이 Blob — 텍스트로 풀어서 메시지 추출
    let msg = e.message;
    try { msg = JSON.parse(await e.response?.data?.text?.())?.error || msg; } catch (_) { /* 무시 */ }
    ElMessage.error("내려받기 실패: " + msg);
  } finally {
    downloading.value = false;
  }
}

const canShowConsent = computed(() => {
  return detail.value && ['PENDING', 'READ'].includes(detail.value.status);
});

onMounted(async () => {
  loading.value = true;
  try {
    detail.value = await contractStore.fetchMyContractDetail(partyId);

    // 동의 항목 초기화
    if (detail.value.consent_items) {
      detail.value.consent_items.forEach((_, idx) => {
        consentChecked.value[`item_${idx}`] = false;
      });
    }

    // 열람 시작 기록
    if (['PENDING', 'READ'].includes(detail.value.status)) {
      await contractStore.recordRead(partyId);
      sessionStart.value = Date.now();
    }
  } finally {
    loading.value = false;
  }
});

// 이번 세션 열람시간을 서버에 반영하고 세션을 닫는다.
// 화면이 keep-alive 탭이라 onBeforeUnmount가 안 올 수 있으므로 onDeactivated에서도 호출.
async function flushReadSession() {
  if (!sessionStart.value) return 0;
  const seconds = Math.floor((Date.now() - sessionStart.value) / 1000);
  sessionStart.value = null;
  if (['PENDING', 'READ'].includes(detail.value?.status)) {
    try {
      await contractStore.recordClose(partyId, seconds);
      if (detail.value) detail.value.total_read_seconds = (detail.value.total_read_seconds || 0) + seconds;
    } catch (e) {
      // 페이지 이탈 시 실패해도 무시
    }
  }
  return seconds;
}

onMounted(() => { ticker = setInterval(() => { now.value = Date.now(); }, 1000); });
onBeforeUnmount(() => { clearInterval(ticker); flushReadSession(); });
onDeactivated(flushReadSession);
onActivated(async () => {
  // 탭으로 다시 돌아오면 새 열람 세션 시작
  if (!sessionStart.value && ['PENDING', 'READ'].includes(detail.value?.status)) {
    try { await contractStore.recordRead(partyId); } catch (e) { /* 무시 */ }
    sessionStart.value = Date.now();
  }
});

function handleScroll() {
  // iframe 스크롤 추적은 제한적 - 수동 완료 버튼 사용
}

async function markScrollComplete() {
  await contractStore.recordScrollComplete(partyId);
  detail.value = await contractStore.fetchMyContractDetail(partyId);
  ElMessage.success("열람 완료가 기록되었습니다.");
}

async function handleConsent() {
  if (detail.value.require_signature && !signatureData.value) {
    ElMessage.warning("서명란에 서명해 주세요.");
    return;
  }
  try {
    await ElMessageBox.confirm(
      "동의하시겠습니까? 동의 후에는 철회할 수 없습니다.",
      "동의 확인",
      { type: "warning" }
    );

    // 현재 세션 시간 계산
    const sessionSeconds = sessionStart.value
      ? Math.floor((Date.now() - sessionStart.value) / 1000)
      : 0;
    sessionStart.value = null;

    consenting.value = true;
    await contractStore.submitConsent(partyId, consentChecked.value, sessionSeconds, signatureData.value);
    ElMessage.success("동의가 완료되었습니다.");
    detail.value = await contractStore.fetchMyContractDetail(partyId);
  } catch (e) {
    if (e !== "cancel") {
      const msg = e.response?.data?.errors?.join(", ") || e.response?.data?.error || "동의 처리 실패";
      ElMessage.error(msg);
    }
  } finally {
    consenting.value = false;
  }
}

async function handleReject() {
  try {
    const { value: reason } = await ElMessageBox.prompt(
      "거부 사유를 입력해주세요.",
      "거부",
      { confirmButtonText: "거부 확인", cancelButtonText: "취소", inputType: "textarea" }
    );

    // 거부 직전까지 읽은 시간도 기록에 남긴다 (거부 후에는 열람종료가 기록되지 않음)
    const sessionSeconds = sessionStart.value ? Math.floor((Date.now() - sessionStart.value) / 1000) : 0;
    sessionStart.value = null;
    await contractStore.submitRejection(partyId, reason, sessionSeconds);
    ElMessage.success("거부가 기록되었습니다.");
    detail.value = await contractStore.fetchMyContractDetail(partyId);
  } catch (e) {
    if (e !== "cancel") ElMessage.error("거부 처리 실패");
  }
}

function statusTag(s) {
  return { PENDING: "info", READ: "warning", CONSENTED: "success", REJECTED: "danger", EXPIRED: "" }[s] || "";
}
</script>

<style scoped>
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.info-card { margin-bottom: 16px; }
.info-card h3 { margin: 0; font-size: 15px; }
.pdf-viewer { border: 1px solid #eee; border-radius: 4px; overflow: hidden; }
.no-file { padding: 40px; text-align: center; color: #999; }
.read-status { display: flex; align-items: center; gap: 12px; padding: 12px 0; }
.terms-note { margin-top: var(--sw-space-2); font-size: var(--sw-font-sm); color: var(--el-text-color-secondary); }
.signature-block { margin: var(--sw-space-4) 0; padding: var(--sw-space-3); background: var(--el-fill-color-lighter); border-radius: 4px; }
.signature-title { font-weight: 600; margin-bottom: var(--sw-space-1); }
.signature-desc { font-size: var(--sw-font-sm); color: var(--el-text-color-secondary); margin-bottom: var(--sw-space-2); }
.signed-hint { margin-top: var(--sw-space-2); font-size: var(--sw-font-sm); color: var(--el-text-color-secondary); }
.signed-pending { color: var(--el-text-color-secondary); }
.read-time { font-size: 13px; color: #666; }
.consent-items { margin-bottom: 16px; }
.consent-item { padding: 8px 0; border-bottom: 1px solid #f0f0f0; }
.consent-item:last-child { border-bottom: none; }
.consent-actions { display: flex; gap: 12px; justify-content: center; padding-top: 16px; border-top: 1px solid #eee; }
</style>
